import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'
import { checkCustomerNitIsValid, SelectOption } from '@ecommerce/shared'
import { sendMessageToSentry, ErrorLevel } from '@ecommerce/chile-customer-webapp/src/utils/sentry'
import useDebounce from '../../../hooks/useDebounce'
import Select from '../Select'
import TextField from '../TextField'
import { showToast } from '../Notification'
import { checkCustomerDniExist } from '../../../services/SignUp'
import { log } from '../../../utils/log'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  margin-bottom: 16px;

  input[type='number'] {
    -moz-appearance: textfield;
  }
`

export const ContainerCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .title {
    font-size: 12px;
    line-height: 16px;
    color: #030f1c;
    margin: 0;
  }
`

export const FormRow = styled.div`
  display: flex;
  gap: 16px;

  & > * {
    flex: 1;
  }

  .complement-field {
    flex: 0.5;
  }
`

export const ForeignerCard = styled(TextField)`
  width: 51px;
`

export const ContainerInput = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`

interface Props {
  isVisible: boolean
  types: SelectOption[]
  selectedDocumentType: string
  setSelectedDocumentType: (selectedDocumentType: string) => void
  setIsCheckingDni: (setIsCheckingDni: boolean) => void
  dni?: string
  isRecovery?: boolean
  isGuest?: boolean
}

const DocumentType = ({
  isVisible,
  types,
  selectedDocumentType,
  setSelectedDocumentType,
  setIsCheckingDni,
  dni,
  isRecovery,
  isGuest = false,
}: Props) => {
  const methods = useFormContext()

  const { control, errors, setValue, setError, trigger, watch, clearErrors, getValues, reset } = methods

  const documentType = watch('documentType')
  const [loading, setLoading] = useState(false)
  const [dniValue, setDniValue] = useState(dni ?? '')
  const [successMessage, setSuccessMessage] = useState<string>()

  const dniDebounce = useDebounce(dniValue, 1500)

  const formatDniBolivia = () => types.find((dt) => dt.value === documentType)?.label || types[0]?.label

  const checkDni = async () => {
    try {
      if (documentType !== 'otros') {
        if (dni !== getValues().dni) {
          const response = await checkCustomerDniExist({ dni: getValues().dni })
          if (response.status === 200) {
            if (response.data.customerExists && !isRecovery) {
              showToast({
                content: `Ya tenemos este ${formatDniBolivia()} asociado a una cuenta.`,
                title: `Ya existe este ${formatDniBolivia()}`,
                type: 'error',
              })
              setError('dni', {
                type: 'manual',
                message: `Ya tenemos este ${formatDniBolivia()} asociado a una cuenta.`,
              })
            }
            if (!response.data.customerExists && isRecovery) {
              showToast({
                content: `No hay una cuenta asociada a ese ${formatDniBolivia()}.`,
                title: `No tenemos ese ${formatDniBolivia()}`,
                type: 'error',
              })
              setError('dni', {
                type: 'manual',
                message: `No hay una cuenta asociada a ese ${formatDniBolivia()}.`,
              })
            }
          }
        }
      }
    } catch (error) {
      log.error('ERROR CHECKING DNI: ', error)
      showToast({
        content: `Ha ocurrido un error en la búsqueda del documento seleccionado`,
        title: `Ha ocurrido un error`,
        type: 'error',
      })
      sendMessageToSentry({
        message: `checkDni: ${error?.message ?? ''}`,
        level: ErrorLevel.Warning,
        page: 'editBolivia',
        metadata: { error: { ...error } },
      })
    }
    setIsCheckingDni(false)
  }

  const checkNitIsValid = async () => {
    setLoading(true)
    try {
      clearErrors('dni')
      const response = await checkCustomerNitIsValid({ nit: getValues().dni })
      if (response.data.validate) {
        setSuccessMessage(`NIT válido.`)
      } else {
        setError('dni', {
          type: 'manual',
          message: 'Ingresa un número de NIT válido',
        })
      }
    } catch (error) {
      log.error('ERROR CHECKING NIT: ', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (dniDebounce !== '' && !isGuest) {
      checkDni()
    }
    if (dniDebounce !== '' && documentType === 'nit') {
      checkNitIsValid()
    }
  }, [dniDebounce])

  const onDocumentTypeChange = async (selected: string) => {
    setSelectedDocumentType(selected)
    setValue('documentType', selected, { shouldDirty: true })
    reset({
      ...getValues(),
      dni: undefined,
      dniComplement: undefined,
    })
    setDniValue('')
    await trigger('documentType')
  }

  const onDniChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (documentType !== 'nit') {
      clearErrors('dni')
    } else {
      setSuccessMessage(undefined)
    }
    const { value } = event.target
    setValue('dni', value, { shouldDirty: true })
    await trigger('dni')
    setDniValue(value)
    if (documentType !== 'otros') {
      setIsCheckingDni(false)
    }
  }

  const onDniComplementChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setValue('dniComplement', value, { shouldDirty: true })
    await trigger('dniComplement')
  }

  const getStatus = () => {
    if (errors.dni) return 'error'
    if (successMessage) return 'success'
    return undefined
  }

  return (
    <Container>
      {isVisible && (
        <Controller
          control={control}
          name="documentType"
          render={({ value }) => (
            <Select
              label={isRecovery ? 'Método de recuperación' : 'Documento de identificación'}
              disabled={!isVisible}
              placeholder={
                value
                  ? types.find((dt) => dt.value === value)?.label || types[0].label
                  : 'Selecciona tu documento identificación'
              }
              options={types}
              onSelect={(selected) => onDocumentTypeChange(selected)}
            />
          )}
        />
      )}

      <FormRow>
        <ContainerCard>
          {selectedDocumentType === 'carnet_extranjeria' && <p className="title">Carnet de extranjería</p>}

          <ContainerInput>
            {selectedDocumentType === 'carnet_extranjeria' && <ForeignerCard value="-E" type="text" disabled />}

            {selectedDocumentType && (
              <Controller
                control={control}
                name="dni"
                render={({ value }) => (
                  <TextField
                    label={selectedDocumentType !== 'carnet_extranjeria' ? formatDniBolivia() : ''}
                    status={getStatus()}
                    errorMessage={errors.dni?.message}
                    value={dniValue ?? ''}
                    type={documentType === 'ci' || documentType === 'nit' ? 'number' : 'text'}
                    disabled={!isVisible}
                    onChange={onDniChange}
                    min="1"
                    onWheel={(e) => e.currentTarget.blur()}
                    successMessage={successMessage}
                    loading={loading}
                    className="dni"
                  />
                )}
              />
            )}
          </ContainerInput>
        </ContainerCard>

        {(getValues().dniComplement || (isVisible && selectedDocumentType === 'ci')) && !isRecovery && (
          <>
            <Controller
              name="dniComplement"
              control={control}
              render={({ value }) => (
                <TextField
                  label="Complemento"
                  placeholder="ej:1A"
                  maxLength={2}
                  status={!errors.dniComplement ? undefined : 'error'}
                  errorMessage={errors.dniComplement?.message}
                  value={value ?? ''}
                  disabled={!isVisible}
                  onChange={onDniComplementChange}
                  tooltipText="Valor que entrega el SEGIP en caso de cédulas de identidad con número duplicado"
                  tooltipTipPosition="left"
                />
              )}
            />
          </>
        )}
      </FormRow>
    </Container>
  )
}

export default DocumentType
